import {
  CountryCodeSelector,
  getPhoneParts,
  isValidPhoneForSelectedCountry,
} from "@/components/auth/country-code-selector";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Input } from "@/components/ui/input.tsx";
import { UserDetailsCard } from "@/components/users/user-details/user-details-card.tsx";
import { User } from "@/lib/api/user.ts";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user.ts";
import { REDACTED_LABEL } from "@/lib/redacted-display";
import { useState } from "react";

interface UserParentDetailsProps {
  user: User;
}

export function UserParentDetails({ user }: UserParentDetailsProps) {
  const ability = useAbility();
  const canUpdateUsers =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");
  const redactedFields = new Set(user.redacted_fields ?? []);
  const guardianNameRedacted = redactedFields.has("guardian_name");
  const emergencyPhoneRedacted = redactedFields.has("emergency_phone");
  const emailRedacted = redactedFields.has("email");
  const initialPhoneParts = getPhoneParts(user.emergency_phone || "");
  const [countryCode, setCountryCode] = useState(
    initialPhoneParts.countryCode,
  );
  const { handleUpdate, form, fieldState } = useUpdateUser({
    userId: user.id,
    guardianName: user.guardian_name ?? undefined,
    emergencyPhone: initialPhoneParts.phoneNumber,
    email: user.email || "",

    profile: {
      metadata: {
        parentConsent: user.profile?.metadata?.parentConsent,
        parentOptOutReason: user.profile?.metadata?.parentOptOutReason,
      },
    },
  });

  const isEmergencyPhoneDisabled =
    !canUpdateUsers ||
    Boolean(fieldState.emergencyPhone) ||
    emergencyPhoneRedacted;

  const saveEmergencyPhone = (
    nationalNumber: string,
    selectedCountryCode: string,
  ) => {
    if (emergencyPhoneRedacted) return;

    form.clearErrors("emergencyPhone");
    if (!nationalNumber) {
      handleUpdate("emergencyPhone", "");
      return;
    }

    const fullPhoneNumber = `${selectedCountryCode}${nationalNumber}`;
    if (!isValidPhoneForSelectedCountry(fullPhoneNumber, selectedCountryCode)) {
      form.setError("emergencyPhone", {
        message: "Please enter a valid phone number",
      });
      return;
    }

    handleUpdate("emergencyPhone", fullPhoneNumber);
  };

  return (
    <UserDetailsCard>
      <Form {...form}>
        <form className="flex flex-col gap-4">
          <FormField
            disabled={
              !canUpdateUsers || fieldState.guardianName || guardianNameRedacted
            }
            control={form.control}
            name="guardianName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      guardianNameRedacted ? REDACTED_LABEL : "Enter parent name"
                    }
                    {...field}
                    value={guardianNameRedacted ? "" : (field.value ?? "")}
                    onBlur={() => {
                      if (guardianNameRedacted) return;
                      handleUpdate("guardianName", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="emergencyPhone"
            disabled={isEmergencyPhoneDisabled}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Phone number</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <CountryCodeSelector
                      value={countryCode}
                      onChange={(value) => {
                        setCountryCode(value);
                        const nationalNumber = form.getValues("emergencyPhone") ?? "";
                        if (nationalNumber) {
                          saveEmergencyPhone(nationalNumber, value);
                        }
                      }}
                      disabled={isEmergencyPhoneDisabled}
                      className="h-10 rounded-md border-input bg-background"
                    />
                    <Input
                      placeholder={
                        emergencyPhoneRedacted
                          ? REDACTED_LABEL
                          : "Enter phone"
                      }
                      inputMode="tel"
                      type="tel"
                      {...field}
                      value={emergencyPhoneRedacted ? "" : (field.value ?? "")}
                      onChange={(event) => {
                        field.onChange(
                          event.target.value.replace(/[^0-9]/g, ""),
                        );
                        form.clearErrors("emergencyPhone");
                      }}
                      onBlur={() => {
                        if (emergencyPhoneRedacted) return;
                        saveEmergencyPhone(field.value ?? "", countryCode);
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            disabled
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder={emailRedacted ? REDACTED_LABEL : "Enter email"}
                    inputMode="email"
                    {...field}
                    value={emailRedacted ? "" : (field.value ?? "")}
                    onBlur={() => {
                      handleUpdate("email", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="profile.metadata.parentConsent"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">
                  Parent Consent (EDIT WITH CARE)
                </FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    const currentMetadata = form.getValues("profile.metadata");
                    handleUpdate("profile", {
                      metadata: {
                        ...currentMetadata,
                        parentConsent: value as "Yes" | "No",
                        parentOptOutReason: form.getValues(
                          "profile.metadata.parentOptOutReason",
                        ),
                      },
                    });
                  }}
                  value={field.value}
                  disabled={!canUpdateUsers}
                >
                  <FormControl>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select consent status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="profile.metadata.parentOptOutReason"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">
                  Opt-out Reason
                </FormLabel>
                <FormControl>
                  <Input placeholder="No opt-out reason given" {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </UserDetailsCard>
  );
}